import { ColDef, RowClickedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { aggridHeaderClass } from 'src/components/common/common';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import Grid_Touch from 'src/components/common/gridWrapper/Grid_Touch';
import { useCommonCodeStore } from 'src/store/useCommonCodeStore';
import { GridCustomColumnDefs } from 'src/utils/grid/GridCustomColumnDefs';
import { buildDefaultColumnStateFromDefs } from 'src/utils/grid/columnPinUtils';
import { useGridSettingContent } from 'src/utils/grid/GridSettingContent';
import { defaultColDef, getDefaultRowIndexColumnConfig } from 'src/utils/grid/gridStyleUtils';

import { MST2010_R01Dto } from './Dto_MST2010_R01';
import { useMST2010_R01Store } from './Store_MST2010_R01';
import { onlyTouchGridStyle } from 'src/utils/grid/gridFunctionUtil';

const COLUMN_STATE_KEY = 'MST2010_R01-grid-column-state';

interface Props {
	gridRef: React.RefObject<AgGridReact<MST2010_R01Dto> | null>;
	defaultMST2010_R01Row: MST2010_R01Dto;
}

export const Table_MST2010_R01 = (props: Props) => {
	const { gridRef, defaultMST2010_R01Row } = props;

	const {
		MST2010_R01List,
		selectedMST2010_R01,
		setSelectedMST2010_R01,
		initSelectedMST2010_R01,
		columnState,
		setColumnState,
		scrollTop, setScrollTop,
	} = useMST2010_R01Store();

	const { areaCdList } = useCommonCodeStore();

	const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

	const {
		getRowId,
		settingRowIndex,
		createRowClassRules,
		paddedGridData,
		ensureSelectedRow,
	} = useGridSettingContent({
		gridRef,
		key: 'customCd',
		gridData: MST2010_R01List,
		defaultGridData: defaultMST2010_R01Row,
		selectedRow: selectedMST2010_R01,
	});

	const headerNames: Record<string, string> = {
		rowIndex: '',
		customCd: '거래처',
		customNm: '상호',
		regNo: '사업자번호',
		owner: '대표자',
		areaCd: '관리지역',
		telNo: '전화번호',
		address: '주소',
		faxNo: '팩스',
		businessType: '업태',
		businessClass: '종목',
	};

	const getCellStyle = useCallback((params: unknown, key: string) => onlyTouchGridStyle(key, 'customCd')(params), []);

	const baseColumnDefs: ColDef<MST2010_R01Dto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '거래처', colId: 'customCd', field: 'customCd', width: 89,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'customCd'),
		},
		{
			headerName: '상호', colId: 'customNm', field: 'customNm', width: 119,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'customNm'),
		},
		{
			headerName: '사업자번호', colId: 'regNo', field: 'regNo', width: 104,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'regNo'),
		},
		{
			headerName: '대표자', colId: 'owner', field: 'owner', width: 155,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'owner'),
		},
		{
			headerName: '관리지역', colId: 'areaCd', field: 'areaCd', width: 82,
			headerClass: aggridHeaderClass,
			valueFormatter: (p) => {
				const areaCd = p.data?.areaCd;
				if (areaCd === '' || areaCd === 'area_cd' || areaCd === undefined) return '';
				const areaItem = areaCdList.find((value) => value.minorCd === areaCd);
				return areaItem?.codeNm || '';
			},
			cellStyle: (p) => getCellStyle(p, 'areaCd'),
		},
		{
			headerName: '전화번호', colId: 'telNo', field: 'telNo', width: 109,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'telNo'),
		},
		{
			headerName: '주소', colId: 'address', field: 'address', width: 303,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'address'),
		},
		{
			headerName: '팩스', colId: 'faxNo', field: 'faxNo', width: 118,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'faxNo'),
		},
		{
			headerName: '업태', colId: 'businessType', field: 'businessType', width: 117,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'businessType'),
		},
		{
			headerName: '종목', colId: 'businessClass', field: 'businessClass', width: 134,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'businessClass'),
		},
	], [getCellStyle, areaCdList]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
	, [baseColumnDefs]);

	const columnDefs: ColDef<MST2010_R01Dto>[] = useMemo(() => {
		if (columnState === null) return baseColumnDefs;
		return GridCustomColumnDefs<MST2010_R01Dto>({
			baseColumnDefs,
			columnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [columnState, settingRowIndex, baseColumnDefs, headerNames]);

	const handleRowClick = useCallback((e: RowClickedEvent<MST2010_R01Dto>) => {
		if (e.node.rowPinned === 'bottom') return;
		const rowData = e.data;
		if (!rowData) return;

		const isSelected = selectedMST2010_R01?.customCd === rowData.customCd;
		setSelectedMST2010_R01(isSelected ? undefined : rowData);
	}, [selectedMST2010_R01, setSelectedMST2010_R01]);

	const pinnedBottomRow = useMemo(() => ([{
		rowIndex: '',
		customCd: `${MST2010_R01List.length.toLocaleString()}건`,
		editable: false,
	}]), [MST2010_R01List.length]);

	useEffect(() => { ensureSelectedRow(); }, [MST2010_R01List, selectedMST2010_R01]);

	return (
		<GridContextWrapper<MST2010_R01Dto>
			gridRef={gridRef}
			paddedGridData={paddedGridData}
			initSelected={initSelectedMST2010_R01}
			defaultRowData={defaultMST2010_R01Row}
			baseColumnDefs={columnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={setColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			<Grid_Touch<MST2010_R01Dto>
				ref={gridRef}
				rowData={paddedGridData}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}
				getRowId={getRowId}
				rowClassRules={createRowClassRules<MST2010_R01Dto>({
					idKey: 'customCd',
					selectedList: selectedMST2010_R01 ? [selectedMST2010_R01.customCd] : [],
					deletedList: [],
				})}
				onRowClicked={handleRowClick}
				onCellKeyDown={undefined}
				pinnedBottomRowData={pinnedBottomRow}
				setHoveredRowIndex={setHoveredRowIndex}
				columnStateKey={COLUMN_STATE_KEY}
				setColumnState={setColumnState}
				scrollTop={scrollTop}
				setScrollTop={setScrollTop}
			/>
		</GridContextWrapper>
	);
};
